/**
 * Phase 66 Plan 06 — `useCircuitId()` REAL implementation (replaces Plan 02 stub).
 *
 * Single source of truth for the active tenant ID:
 *
 *   1. URL `?circuit_id=...` (canonical, deep-linkable, shareable)
 *   2. localStorage `protos_circuit_id` (fallback for new tabs)
 *   3. null (super_admin "All circuits" or no selection)
 *
 * The export signature is STABLE — every Plan 03/04/05a/05b query hook
 * already imports `useCircuitId()`, so swapping the body to read URL +
 * localStorage activates multi-tenant cache scoping across the entire
 * app without further code changes (qk.* factories already include
 * circuit_id in every cached entry's key).
 *
 * Pitfall 8 (RESEARCH §Multi-tenant cache-key isolation): on tenant
 * switch, the URL change re-keys every TanStack Query entry, BUT the
 * defense-in-depth `queryClient.invalidateQueries()` call in
 * `useSetCircuitId()` forces a refetch even for any hand-rolled key
 * that may have slipped through — preventing leakage between tenants.
 *
 * Threat T-66-06-01 (Information Disclosure via stale queryKey): mitigated
 * by `invalidateQueries()` on every setCircuitId.
 *
 * @see CONTEXT.md D-13 — Multi-tenant UI (topbar dropdown + URL ?circuit_id)
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "protos_circuit_id";

/** Returns the currently active circuit_id (URL > localStorage > null). */
export function useCircuitId(): string | null {
  const [params] = useSearchParams();
  const fromUrl = params.get("circuit_id");
  if (fromUrl) return fromUrl;
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY);
}

/**
 * Setter hook: returns a callback that updates BOTH URL search param
 * AND localStorage, then invalidates every TanStack Query entry to
 * defend against any hand-rolled cache keys that omit circuit_id.
 */
export function useSetCircuitId(): (id: string | null) => void {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params);
      if (id) {
        next.set("circuit_id", id);
        setParams(next, { replace: false });
        try {
          localStorage.setItem(STORAGE_KEY, id);
        } catch {
          // Storage may be unavailable (private mode); URL still wins.
        }
      } else {
        next.delete("circuit_id");
        setParams(next, { replace: false });
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignored
        }
      }
      // Defense-in-depth: re-fetch every active query (Pitfall 8 + T-66-06-01).
      queryClient.invalidateQueries();
    },
    [params, setParams, queryClient],
  );
}

export { STORAGE_KEY as CIRCUIT_ID_STORAGE_KEY };
